import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import './InvoiceGeneratorPage.css';

const PEPPER = 'vionex-client-guard-v1';
const EXPECTED_HASHES = {
  email: 'ec1a9a4ef33a0f214d962f5ee9a6d4dab0bb57dc49f91ec2fa0631fe59b8644c',
  password: '01dd80c601f95a3572efa0bc56d3a778fe1e605de0ecb07d7ceb1d4488e177e6',
  pin: '2b6eab71812a3a7589d36cca18eb0840c220bdc3051d59cfda1b937dfecf2d41'
};
const MAX_ATTEMPTS = 5;
const COOLDOWN_MS = 60_000;

const defaultInvoice = {
  invoiceNumber: 'INV-20260604',
  invoiceDate: '2026-06-04',
  dueDate: '2026-06-04',
  currencyCode: 'INR',
  from: {
    name: 'Shashank Dewangan',
    phone: '7725008610',
    company: 'Vionex Labs',
    addressLine1: '227, L Block, Street no, 7A',
    addressLine2: 'Mahipalpur, New Delhi, 110037'
  },
  billTo: {
    company: 'FR Solutions Pvt. Ltd.',
    addressLine1: 'DLF Prime Towers, 031,',
    addressLine2: 'Mata Mohalla, Pocket F, Okhla Phase I,',
    addressLine3: 'Okhla Industrial Estate,',
    addressLine4: 'New Delhi, 110020'
  },
  items: [
    {
      id: 1,
      description: 'FR Solutions Website Development',
      amount: '45000'
    }
  ],
  taxRate: '0',
  amountReceived: '0',
  bank: {
    accountHolder: 'Shashank Dewangan',
    bankName: 'SBI',
    accountNumber: '44333468350',
    ifsc: 'SBIN0012326'
  },
  signatureName: 'Shashank Dewangan',
  signatureTitle: 'Founder, Vionex Labs',
  signatureImage: null,
  showSignatureBlock: true,
  notes: ''
};

function normalizeAmount(value) {
  const normalized = String(value || '').replace(/[^0-9.]/g, '');
  const numeric = Number.parseFloat(normalized);
  return Number.isFinite(numeric) ? numeric : 0;
}

function formatMoney(amount, currencyCode) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currencyCode,
    maximumFractionDigits: 2
  }).format(amount);
}

function formatDate(dateString) {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
}

function constantTimeEqual(a, b) {
  if (typeof a !== 'string' || typeof b !== 'string') return false;
  const len = Math.max(a.length, b.length);
  let result = 0;
  for (let i = 0; i < len; i += 1) {
    const ac = i < a.length ? a.charCodeAt(i) : 0;
    const bc = i < b.length ? b.charCodeAt(i) : 0;
    result |= ac ^ bc;
  }
  return result === 0 && a.length === b.length;
}

async function sha256Hex(input) {
  const encoded = new TextEncoder().encode(input);
  const digest = await window.crypto.subtle.digest('SHA-256', encoded);
  const bytes = Array.from(new Uint8Array(digest));
  return bytes.map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function validateCredentials(email, password, pin) {
  const normalizedEmail = email.trim().toLowerCase();
  const normalizedPin = pin.trim();

  const [emailHash, passwordHash, pinHash] = await Promise.all([
    sha256Hex(`${normalizedEmail}::email::${PEPPER}`),
    sha256Hex(`${password}::password::${PEPPER}`),
    sha256Hex(`${normalizedPin}::pin::${PEPPER}`)
  ]);

  return (
    constantTimeEqual(emailHash, EXPECTED_HASHES.email)
    && constantTimeEqual(passwordHash, EXPECTED_HASHES.password)
    && constantTimeEqual(pinHash, EXPECTED_HASHES.pin)
  );
}

function InvoiceGeneratorPage() {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [lockUntil, setLockUntil] = useState(0);
  const [authError, setAuthError] = useState('');
  const [loginForm, setLoginForm] = useState({ email: '', password: '', pin: '' });

  const [invoice, setInvoice] = useState(defaultInvoice);
  const [isExporting, setIsExporting] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [savedInvoices, setSavedInvoices] = useState([]);
  const [showSavedList, setShowSavedList] = useState(false);
  const invoiceRef = useRef(null);
  const signatureImageInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const lockRemainingSeconds = Math.max(0, Math.ceil((lockUntil - Date.now()) / 1000));

  const financials = useMemo(() => {
    const subtotal = invoice.items.reduce((sum, item) => sum + normalizeAmount(item.amount), 0);
    const taxRate = normalizeAmount(invoice.taxRate);
    const taxAmount = subtotal * (taxRate / 100);
    const total = subtotal + taxAmount;
    const received = normalizeAmount(invoice.amountReceived);
    const balanceDue = total - received;

    return {
      subtotal,
      taxRate,
      taxAmount,
      total,
      received,
      balanceDue
    };
  }, [invoice.items, invoice.taxRate, invoice.amountReceived]);

  const updateInvoiceField = (field, value) => {
    setInvoice((prev) => ({ ...prev, [field]: value }));
  };

  const updateInvoiceGroupField = (group, field, value) => {
    setInvoice((prev) => ({
      ...prev,
      [group]: {
        ...prev[group],
        [field]: value
      }
    }));
  };

  const updateItem = (id, field, value) => {
    setInvoice((prev) => ({
      ...prev,
      items: prev.items.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    }));
  };

  const addItem = () => {
    setInvoice((prev) => {
      const nextId = prev.items.length ? Math.max(...prev.items.map((item) => item.id)) + 1 : 1;
      return {
        ...prev,
        items: [...prev.items, { id: nextId, description: 'New Service Item', amount: '0' }]
      };
    });
  };

  const removeItem = (id) => {
    setInvoice((prev) => {
      if (prev.items.length <= 1) return prev;
      return { ...prev, items: prev.items.filter((item) => item.id !== id) };
    });
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    setAuthError('');

    if (Date.now() < lockUntil) {
      setAuthError(`Too many attempts. Try again in ${lockRemainingSeconds}s.`);
      return;
    }

    const email = loginForm.email.trim();
    const password = loginForm.password;
    const pin = loginForm.pin.trim();

    if (!email.includes('@')) {
      setAuthError('Enter a valid email address.');
      return;
    }
    if (!password) {
      setAuthError('Password is required.');
      return;
    }
    if (!/^\d{4}$/.test(pin)) {
      setAuthError('PIN must be exactly 4 digits.');
      return;
    }

    setIsAuthLoading(true);
    try {
      const ok = await validateCredentials(email, password, pin);
      if (ok) {
        setIsUnlocked(true);
        setAttempts(0);
        setLockUntil(0);
        setLoginForm({ email: '', password: '', pin: '' });
        return;
      }

      const nextAttempt = attempts + 1;
      setAttempts(nextAttempt);
      if (nextAttempt >= MAX_ATTEMPTS) {
        setLockUntil(Date.now() + COOLDOWN_MS);
        setAttempts(0);
        setAuthError('Too many failed attempts. Login is temporarily locked for 60 seconds.');
      } else {
        setAuthError(`Invalid credentials. ${MAX_ATTEMPTS - nextAttempt} attempt(s) left.`);
      }
    } catch (error) {
      setAuthError('Unable to validate credentials. Please try again.');
    } finally {
      setIsAuthLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!invoiceRef.current) return;

    setDownloadError('');
    setIsExporting(true);

    try {
      const canvas = await html2canvas(invoiceRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff'
      });

      const imageData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const printableWidth = pageWidth - margin * 2;
      const printableHeight = pageHeight - margin * 2;
      const imageHeight = (canvas.height * printableWidth) / canvas.width;

      let heightLeft = imageHeight;
      let yOffset = margin;

      pdf.addImage(imageData, 'PNG', margin, yOffset, printableWidth, imageHeight, undefined, 'FAST');
      heightLeft -= printableHeight;

      while (heightLeft > 0) {
        pdf.addPage();
        yOffset = margin - (imageHeight - heightLeft);
        pdf.addImage(imageData, 'PNG', margin, yOffset, printableWidth, imageHeight, undefined, 'FAST');
        heightLeft -= printableHeight;
      }

      const cleanNumber = (invoice.invoiceNumber || 'invoice').replace(/\s+/g, '-').toLowerCase();
      pdf.save(`invoice-${cleanNumber}.pdf`);
    } catch (error) {
      setDownloadError('PDF export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleSignatureImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64String = e.target?.result;
      if (typeof base64String === 'string') {
        updateInvoiceField('signatureImage', base64String);
      }
    };
    reader.readAsDataURL(file);
  };

  const clearSignatureImage = () => {
    updateInvoiceField('signatureImage', null);
    if (signatureImageInputRef.current) {
      signatureImageInputRef.current.value = '';
    }
  };

  const relockSession = () => {
    setIsUnlocked(false);
    setAuthError('Session locked. Re-enter credentials to continue.');
  };

  const handleSaveInvoiceData = () => {
    const dataStr = JSON.stringify(invoice, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `invoice-data-${invoice.invoiceNumber || 'backup'}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleLoadInvoiceData = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const loadedData = JSON.parse(e.target?.result || '{}');
        setInvoice(loadedData);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        alert('Failed to load invoice data. Please ensure the file is a valid JSON export.');
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className={`invoice-page ${isExporting ? 'is-exporting' : ''}`}>
      <header className="invoice-topbar">
        <div>
          <h1>Invoice Generator</h1>
          <p>Protected internal invoicing utility for Vionex Labs.</p>
        </div>
        <div className="invoice-topbar-actions">
          <Link className="invoice-secondary-btn" to="/">Back to Portfolio</Link>
          {isUnlocked && (
            <button type="button" className="invoice-secondary-btn" onClick={relockSession}>
              Lock Session
            </button>
          )}
        </div>
      </header>

      {!isUnlocked ? (
        <section className="auth-shell" aria-label="Invoice access gate">
          <form className="auth-card" onSubmit={handleLoginSubmit}>
            <h2>Authorized Access</h2>
            <p>Enter approved Email, Password, and PIN to open the invoice workspace.</p>

            <label htmlFor="auth-email">Email</label>
            <input
              id="auth-email"
              type="email"
              autoComplete="username"
              value={loginForm.email}
              onChange={(e) => setLoginForm((prev) => ({ ...prev, email: e.target.value }))}
              disabled={isAuthLoading || lockRemainingSeconds > 0}
              required
            />

            <label htmlFor="auth-password">Password</label>
            <input
              id="auth-password"
              type="password"
              autoComplete="current-password"
              value={loginForm.password}
              onChange={(e) => setLoginForm((prev) => ({ ...prev, password: e.target.value }))}
              disabled={isAuthLoading || lockRemainingSeconds > 0}
              required
            />

            <label htmlFor="auth-pin">4-digit PIN</label>
            <input
              id="auth-pin"
              type="password"
              inputMode="numeric"
              maxLength={4}
              value={loginForm.pin}
              onChange={(e) => setLoginForm((prev) => ({ ...prev, pin: e.target.value.replace(/\D/g, '') }))}
              disabled={isAuthLoading || lockRemainingSeconds > 0}
              required
            />

            {authError && <p className="auth-error">{authError}</p>}
            {lockRemainingSeconds > 0 && (
              <p className="auth-lock-note">Login locked for {lockRemainingSeconds}s due to repeated failures.</p>
            )}

            <button type="submit" className="invoice-primary-btn" disabled={isAuthLoading || lockRemainingSeconds > 0}>
              {isAuthLoading ? 'Checking...' : 'Unlock Invoice Generator'}
            </button>
          </form>
        </section>
      ) : (
        <main className="invoice-workspace">
          <aside className="invoice-control-panel" aria-label="Invoice edit controls">
            <div className="control-card">
              <h3>Invoice Meta</h3>
              <label htmlFor="meta-number">Invoice Number</label>
              <input
                id="meta-number"
                value={invoice.invoiceNumber}
                onChange={(e) => updateInvoiceField('invoiceNumber', e.target.value)}
              />

              <label htmlFor="meta-date">Invoice Date</label>
              <input
                id="meta-date"
                type="date"
                value={invoice.invoiceDate}
                onChange={(e) => updateInvoiceField('invoiceDate', e.target.value)}
              />

              <label htmlFor="meta-due-date">Due Date</label>
              <input
                id="meta-due-date"
                type="date"
                value={invoice.dueDate}
                onChange={(e) => updateInvoiceField('dueDate', e.target.value)}
              />
            </div>

            <div className="control-card">
              <h3>Data Management</h3>
              <button type="button" className="invoice-secondary-btn block" onClick={handleSaveInvoiceData}>
                Save as JSON
              </button>
              <label htmlFor="load-invoice" style={{ marginTop: '0.75rem', cursor: 'pointer', display: 'inline-block' }}>
                <input
                  id="load-invoice"
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleLoadInvoiceData}
                  style={{ display: 'none' }}
                />
                <span className="invoice-secondary-btn" onClick={() => fileInputRef.current?.click()}>
                  Load from JSON
                </span>
              </label>
            </div>

            <div className="control-card">
              <h3>From</h3>
              <label htmlFor="from-name">Name</label>
              <input id="from-name" value={invoice.from.name} onChange={(e) => updateInvoiceGroupField('from', 'name', e.target.value)} />

              <label htmlFor="from-phone">Phone</label>
              <input id="from-phone" value={invoice.from.phone} onChange={(e) => updateInvoiceGroupField('from', 'phone', e.target.value)} />

              <label htmlFor="from-company">Company</label>
              <input id="from-company" value={invoice.from.company} onChange={(e) => updateInvoiceGroupField('from', 'company', e.target.value)} />

              <label htmlFor="from-address-1">Address Line 1</label>
              <input id="from-address-1" value={invoice.from.addressLine1} onChange={(e) => updateInvoiceGroupField('from', 'addressLine1', e.target.value)} />

              <label htmlFor="from-address-2">Address Line 2</label>
              <input id="from-address-2" value={invoice.from.addressLine2} onChange={(e) => updateInvoiceGroupField('from', 'addressLine2', e.target.value)} />
            </div>

            <div className="control-card">
              <h3>Bill To</h3>
              <label htmlFor="bill-company">Company</label>
              <input id="bill-company" value={invoice.billTo.company} onChange={(e) => updateInvoiceGroupField('billTo', 'company', e.target.value)} />

              <label htmlFor="bill-address-1">Address Line 1</label>
              <input id="bill-address-1" value={invoice.billTo.addressLine1} onChange={(e) => updateInvoiceGroupField('billTo', 'addressLine1', e.target.value)} />

              <label htmlFor="bill-address-2">Address Line 2</label>
              <input id="bill-address-2" value={invoice.billTo.addressLine2} onChange={(e) => updateInvoiceGroupField('billTo', 'addressLine2', e.target.value)} />

              <label htmlFor="bill-address-3">Address Line 3</label>
              <input id="bill-address-3" value={invoice.billTo.addressLine3} onChange={(e) => updateInvoiceGroupField('billTo', 'addressLine3', e.target.value)} />

              <label htmlFor="bill-address-4">Address Line 4</label>
              <input id="bill-address-4" value={invoice.billTo.addressLine4} onChange={(e) => updateInvoiceGroupField('billTo', 'addressLine4', e.target.value)} />
            </div>

            <div className="control-card">
              <h3>Line Items</h3>
              {invoice.items.map((item, idx) => (
                <div className="item-editor" key={item.id}>
                  <label htmlFor={`item-desc-${item.id}`}>Description {idx + 1}</label>
                  <input
                    id={`item-desc-${item.id}`}
                    value={item.description}
                    onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                  />

                  <label htmlFor={`item-amount-${item.id}`}>Amount</label>
                  <input
                    id={`item-amount-${item.id}`}
                    inputMode="decimal"
                    value={item.amount}
                    onChange={(e) => updateItem(item.id, 'amount', e.target.value)}
                  />

                  <button
                    type="button"
                    className="invoice-link-btn"
                    onClick={() => removeItem(item.id)}
                    disabled={invoice.items.length <= 1}
                  >
                    Remove Item
                  </button>
                </div>
              ))}
              <button type="button" className="invoice-secondary-btn block" onClick={addItem}>Add Item</button>
            </div>

            <div className="control-card">
              <h3>Totals</h3>
              <label htmlFor="tax-rate">Tax Rate %</label>
              <input
                id="tax-rate"
                inputMode="decimal"
                value={invoice.taxRate}
                onChange={(e) => updateInvoiceField('taxRate', e.target.value)}
              />

              <label htmlFor="amount-received">Amount Received</label>
              <input
                id="amount-received"
                inputMode="decimal"
                value={invoice.amountReceived}
                onChange={(e) => updateInvoiceField('amountReceived', e.target.value)}
              />
            </div>

            <div className="control-card">
              <h3>Bank Details</h3>
              <label htmlFor="bank-holder">Account Holder</label>
              <input id="bank-holder" value={invoice.bank.accountHolder} onChange={(e) => updateInvoiceGroupField('bank', 'accountHolder', e.target.value)} />

              <label htmlFor="bank-name">Bank Name</label>
              <input id="bank-name" value={invoice.bank.bankName} onChange={(e) => updateInvoiceGroupField('bank', 'bankName', e.target.value)} />

              <label htmlFor="bank-account">Account Number</label>
              <input id="bank-account" value={invoice.bank.accountNumber} onChange={(e) => updateInvoiceGroupField('bank', 'accountNumber', e.target.value)} />

              <label htmlFor="bank-ifsc">IFSC</label>
              <input id="bank-ifsc" value={invoice.bank.ifsc} onChange={(e) => updateInvoiceGroupField('bank', 'ifsc', e.target.value)} />
            </div>

            <div className="control-card">
              <h3>Signature Section</h3>
              <div className="toggle-row">
                <label htmlFor="show-sig">Show Signature Block</label>
                <input
                  id="show-sig"
                  type="checkbox"
                  checked={invoice.showSignatureBlock}
                  onChange={(e) => updateInvoiceField('showSignatureBlock', e.target.checked)}
                  className="toggle-checkbox"
                />
              </div>

              <label htmlFor="sign-name">Signer Name</label>
              <input id="sign-name" value={invoice.signatureName} onChange={(e) => updateInvoiceField('signatureName', e.target.value)} />

              <label htmlFor="sign-title">Signer Title</label>
              <input id="sign-title" value={invoice.signatureTitle} onChange={(e) => updateInvoiceField('signatureTitle', e.target.value)} />

              <label htmlFor="sig-image">Signature Image (PNG/JPG)</label>
              <input
                id="sig-image"
                type="file"
                ref={signatureImageInputRef}
                accept="image/png,image/jpeg,image/jpg"
                onChange={handleSignatureImageUpload}
                className="file-input"
              />
              {invoice.signatureImage && (
                <div className="signature-preview-block">
                  <img src={invoice.signatureImage} alt="Signature preview" className="signature-preview-img" />
                  <button type="button" className="invoice-danger-btn" onClick={clearSignatureImage}>Remove Image</button>
                </div>
              )}
            </div>

            <div className="control-card">
              <h3>Notes / Terms</h3>
              <label htmlFor="notes">Invoice Notes</label>
              <textarea
                id="notes"
                rows={4}
                value={invoice.notes}
                onChange={(e) => updateInvoiceField('notes', e.target.value)}
              />
            </div>

            {downloadError && <p className="download-error">{downloadError}</p>}

            <button type="button" className="invoice-primary-btn block" onClick={handleDownloadPdf} disabled={isExporting}>
              {isExporting ? 'Preparing PDF...' : 'Download PDF'}
            </button>
          </aside>

          <section className="invoice-preview-wrap" aria-label="Invoice preview">
            <article className="invoice-sheet" ref={invoiceRef}>
              <header className="sheet-header">
                <div className="sheet-brand">
                  <img src="/assets/vionex-labs-logo.png" alt="Vionex Labs" />
                  {/* <a href="https://vionexlabs.com" target="_blank" rel="noopener noreferrer">https://vionexlabs.com</a> */}
                </div>
                <div className="sheet-title-block">
                  <h2>INVOICE</h2>
                  <table>
                    <tbody>
                      <tr>
                        <td>Invoice #</td>
                        <td>{invoice.invoiceNumber}</td>
                      </tr>
                      <tr>
                        <td>Invoice Date</td>
                        <td>{formatDate(invoice.invoiceDate)}</td>
                      </tr>
                      <tr>
                        <td>Due Date</td>
                        <td>{formatDate(invoice.dueDate)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </header>

              <section className="sheet-address-row">
                <div>
                  <h3>FROM</h3>
                  <p>{invoice.from.name}</p>
                  <p>{invoice.from.phone}</p>
                  <p>{invoice.from.company}</p>
                  <p>{invoice.from.addressLine1}</p>
                  <p>{invoice.from.addressLine2}</p>
                </div>
                <div>
                  <h3>BILL TO</h3>
                  <p>{invoice.billTo.company}</p>
                  <p>{invoice.billTo.addressLine1}</p>
                  <p>{invoice.billTo.addressLine2}</p>
                  <p>{invoice.billTo.addressLine3}</p>
                  <p>{invoice.billTo.addressLine4}</p>
                </div>
              </section>

              <table className="items-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Item and Description</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.map((item, idx) => (
                    <tr key={item.id}>
                      <td>{idx + 1}</td>
                      <td>{item.description}</td>
                      <td>{formatMoney(normalizeAmount(item.amount), invoice.currencyCode)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <table className="totals-table">
                <tbody>
                  <tr>
                    <td>Subtotal</td>
                    <td>{formatMoney(financials.subtotal, invoice.currencyCode)}</td>
                  </tr>
                  {financials.taxRate > 0 && (
                    <tr>
                      <td>Tax ({financials.taxRate}%)</td>
                      <td>{formatMoney(financials.taxAmount, invoice.currencyCode)}</td>
                    </tr>
                  )}
                  <tr className="totals-strong">
                    <td>Total</td>
                    <td>{formatMoney(financials.total, invoice.currencyCode)}</td>
                  </tr>
                  <tr className="totals-strong">
                    <td>Balance Due</td>
                    <td>{formatMoney(financials.balanceDue, invoice.currencyCode)}</td>
                  </tr>
                </tbody>
              </table>

              <footer className="sheet-footer">
                <div className="bank-block">
                  <h4>BANK DETAILS</h4>
                  <p><strong>Account Holder:</strong> {invoice.bank.accountHolder}</p>
                  <p><strong>Bank Name:</strong> {invoice.bank.bankName}</p>
                  <p><strong>Account Number:</strong> {invoice.bank.accountNumber}</p>
                  <p><strong>IFSC:</strong> {invoice.bank.ifsc}</p>
                  {invoice.notes && <p className="notes-line"><strong>Notes:</strong> {invoice.notes}</p>}
                </div>

                {invoice.showSignatureBlock && (
                  <div className="signature-block">
                    <p className="signature-label">AUTHORIZED SIGNATURE</p>
                    {invoice.signatureImage ? (
                      <img src={invoice.signatureImage} alt="Signature" className="signature-image-export" />
                    ) : (
                      <div className="signature-line" />
                    )}
                    <p className="signature-name">{invoice.signatureName}</p>
                    <p className="signature-title">{invoice.signatureTitle}</p>
                  </div>
                )}
              </footer>
            </article>
          </section>
        </main>
      )}
    </div>
  );
}

export default InvoiceGeneratorPage;
